'use client';

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { Loader2, Upload } from 'lucide-react';
import type { Accept } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

import type { StorageBucket } from '@kit/shared/storage';
import { Button } from '@kit/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@kit/ui/dialog';
import { Input } from '@kit/ui/input';
import { Label } from '@kit/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@kit/ui/select';
import { Textarea } from '@kit/ui/textarea';
import { Trans } from '@kit/ui/trans';

import type { DocumentTypeOption, DocumentUploadInput } from '../types';
import { isImageFile } from '../utils/file-type-utils';
import { DocumentDropzone } from './document-dropzone';
import {
  DocumentUploadQueue,
  type UploadQueueItem,
  type UploadStatus,
} from './document-upload-queue';

interface DocumentUploaderProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpload: (input: DocumentUploadInput) => Promise<void>; // i18n-ignore TS type signature, not user-facing copy
  documentTypes?: DocumentTypeOption[];
  defaultDocumentType?: string;
  acceptedTypes?: Accept;
  maxFileSize?: number;
  maxFiles?: number;
  dialogTitle?: string;
  dialogDescription?: string;
  showMultipleFiles?: boolean;
  showPreview?: boolean;
  client?: SupabaseClient;
  bucket?: StorageBucket;
  folder?: string;
}

/**
 * Enhanced document upload dialog with drag-drop, queue, and progress tracking
 *
 * @example
 * ```tsx
 * <DocumentUploader
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 *   onUpload={async (input) => {
 *     await uploadDocument(input);
 *   }}
 *   documentTypes={[
 *     { value: 'photo', label: 'Photo' },
 *     { value: 'video', label: 'Video' },
 *   ]}
 *   acceptedTypes={{
 *     'image/*': ['.jpg', '.jpeg', '.png', '.webp'],
 *     'video/*': ['.mp4', '.mov'],
 *   }}
 *   maxFileSize={50 * 1024 * 1024}
 *   showPreview
 * />
 * ```
 */
export function DocumentUploader({
  open,
  onOpenChange,
  onUpload,
  documentTypes,
  defaultDocumentType,
  acceptedTypes = {
    'image/*': ['.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif'],
    'video/*': ['.mp4', '.mov', '.webm'],
    'application/pdf': ['.pdf'],
  },
  maxFileSize = 50 * 1024 * 1024,
  maxFiles = 1,
  dialogTitle,
  dialogDescription,
  showMultipleFiles = false,
  showPreview = true,
}: DocumentUploaderProps) {
  const { t } = useTranslation();
  const id = useId();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [documentType, setDocumentType] = useState(
    defaultDocumentType || documentTypes?.[0]?.value || '',
  );
  const [uploadQueue, setUploadQueue] = useState<UploadQueueItem[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  // Track preview URLs for cleanup on unmount
  const previewUrlsRef = useRef<Set<string>>(new Set());

  // Cleanup preview URLs on unmount to prevent memory leaks
  useEffect(() => {
    // Capture ref for cleanup - we want to revoke URLs accumulated during component lifetime
    const urlsRef = previewUrlsRef;
    return () => {
      urlsRef.current.forEach((url) => {
        URL.revokeObjectURL(url);
      });
      urlsRef.current.clear();
    };
  }, []);

  const effectiveMaxFiles = showMultipleFiles ? maxFiles : 1;

  const resetForm = useCallback(() => {
    setTitle('');
    setDescription('');
    setDocumentType(defaultDocumentType || documentTypes?.[0]?.value || '');
    setUploadQueue([]);
    setIsUploading(false);
  }, [defaultDocumentType, documentTypes]);

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen && !isUploading) {
        resetForm();
      }
      if (!isUploading) {
        onOpenChange(newOpen);
      }
    },
    [isUploading, onOpenChange, resetForm],
  );

  const handleFilesSelected = useCallback((files: File[]) => {
    const newItems: UploadQueueItem[] = files.map((file, index) => {
      const previewUrl = isImageFile(file.type)
        ? URL.createObjectURL(file)
        : undefined;

      // Track URL for cleanup on unmount
      if (previewUrl) {
        previewUrlsRef.current.add(previewUrl);
      }

      return {
        id: `${Date.now()}-${index}`,
        file,
        status: 'pending' as UploadStatus,
        progress: 0,
        previewUrl,
      };
    });

    setUploadQueue((prev) => {
      // Clean up old preview URLs and remove from tracking
      prev.forEach((item) => {
        if (item.previewUrl) {
          URL.revokeObjectURL(item.previewUrl);
          previewUrlsRef.current.delete(item.previewUrl);
        }
      });
      return newItems;
    });

    // Auto-populate title from first file name if empty
    if (files.length > 0 && files[0]) {
      const fileName = files[0].name.replace(/\.[^/.]+$/, '');
      setTitle((prev) => prev || fileName);
    }
  }, []);

  const handleRemove = useCallback((itemId: string) => {
    setUploadQueue((prev) => {
      const item = prev.find((i) => i.id === itemId);
      if (item?.previewUrl) {
        URL.revokeObjectURL(item.previewUrl);
        previewUrlsRef.current.delete(item.previewUrl);
      }
      return prev.filter((i) => i.id !== itemId);
    });
  }, []);

  const handleRetry = useCallback((itemId: string) => {
    setUploadQueue((prev) =>
      prev.map((item) =>
        item.id === itemId
          ? { ...item, status: 'pending' as UploadStatus, error: undefined }
          : item,
      ),
    );
  }, []);

  const updateQueueItem = useCallback(
    (itemId: string, updates: Partial<UploadQueueItem>) => {
      setUploadQueue((prev) =>
        prev.map((item) =>
          item.id === itemId ? { ...item, ...updates } : item,
        ),
      );
    },
    [],
  );

  const handleUpload = useCallback(async () => {
    if (uploadQueue.length === 0 || !title.trim()) return;

    setIsUploading(true);

    // Upload each file in sequence
    for (const item of uploadQueue) {
      if (item.status === 'completed') continue;

      updateQueueItem(item.id, { status: 'uploading', progress: 0 });

      try {
        // Simulate progress for now (actual progress would come from useChunkedUpload)
        updateQueueItem(item.id, { progress: 50 });

        await onUpload({
          file: item.file,
          title:
            uploadQueue.length > 1
              ? `${title} - ${item.file.name}`
              : title.trim(),
          documentType: documentType || undefined,
          description: description.trim() || undefined,
        });

        updateQueueItem(item.id, { status: 'completed', progress: 100 });
      } catch (error) {
        updateQueueItem(item.id, {
          status: 'failed',
          error:
            error instanceof Error
              ? error.message
              : t('documents:queue.uploadFailed'),
        });
      }
    }

    setIsUploading(false);

    // Check if all uploads completed successfully
    const allCompleted = uploadQueue.every(
      (item) =>
        item.status === 'completed' ||
        uploadQueue.find((q) => q.id === item.id)?.status === 'completed',
    );

    if (allCompleted) {
      handleOpenChange(false);
    }
  }, [
    uploadQueue,
    title,
    description,
    documentType,
    onUpload,
    updateQueueItem,
    handleOpenChange,
    t,
  ]);

  const canUpload = useMemo(() => {
    return (
      uploadQueue.length > 0 &&
      title.trim().length > 0 &&
      !isUploading &&
      uploadQueue.some((item) => item.status !== 'completed')
    );
  }, [uploadQueue, title, isUploading]);

  const hasCompletedAll = useMemo(() => {
    return (
      uploadQueue.length > 0 &&
      uploadQueue.every((item) => item.status === 'completed')
    );
  }, [uploadQueue]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="flex max-h-[90vh] flex-col overflow-hidden sm:max-w-lg">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>
            {dialogTitle || (
              <Trans
                i18nKey="documents:uploadDialog.title"
                defaults="Upload Document"
              />
            )}
          </DialogTitle>
          <DialogDescription>
            {dialogDescription || (
              <Trans
                i18nKey="documents:uploadDialog.description"
                defaults="Upload a document with title and optional description"
              />
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 space-y-4 overflow-y-auto py-4 pr-2">
          {/* Drop zone or queue */}
          {uploadQueue.length === 0 ? (
            <DocumentDropzone
              onFilesSelected={handleFilesSelected}
              acceptedTypes={acceptedTypes}
              maxFiles={effectiveMaxFiles}
              maxSize={maxFileSize}
              disabled={isUploading}
              showPreview={false}
            />
          ) : (
            <div className="space-y-3">
              <DocumentUploadQueue
                items={uploadQueue}
                onRemove={handleRemove}
                onRetry={handleRetry}
                showThumbnails={showPreview}
              />
              {!isUploading && !hasCompletedAll && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    uploadQueue.forEach((item) => {
                      if (item.previewUrl) {
                        URL.revokeObjectURL(item.previewUrl);
                        previewUrlsRef.current.delete(item.previewUrl);
                      }
                    });
                    setUploadQueue([]);
                  }}
                  className="w-full"
                >
                  <Trans
                    i18nKey="documents:uploadDialog.changeFiles"
                    defaults="Change Files"
                  />
                </Button>
              )}
            </div>
          )}

          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor={`${id}-title`}>
              <Trans i18nKey="documents:fields.title" defaults="Title" />
            </Label>
            <Input
              id={`${id}-title`}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={isUploading}
              placeholder={t('documents:fields.titlePlaceholder')}
            />
          </div>

          {/* Document type */}
          {documentTypes && documentTypes.length > 0 && (
            <div className="space-y-2">
              <Label>
                <Trans
                  i18nKey="documents:fields.documentType"
                  defaults="Document Type"
                />
              </Label>
              <Select
                value={documentType}
                onValueChange={setDocumentType}
                disabled={isUploading}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {documentTypes.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor={`${id}-description`}>
              <Trans
                i18nKey="documents:fields.description"
                defaults="Description (optional)"
              />
            </Label>
            <Textarea
              id={`${id}-description`}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              disabled={isUploading}
              placeholder={t('documents:fields.descriptionPlaceholder')}
            />
          </div>
        </div>

        <div className="flex flex-shrink-0 justify-end gap-2 border-t pt-4">
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isUploading}
          >
            <Trans i18nKey="common:cancel" defaults="Cancel" />
          </Button>
          {hasCompletedAll ? (
            <Button onClick={() => handleOpenChange(false)}>
              <Trans i18nKey="common:done" defaults="Done" />
            </Button>
          ) : (
            <Button onClick={handleUpload} disabled={!canUpload}>
              {isUploading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  <Trans i18nKey="common:uploading" defaults="Uploading..." />
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  <Trans i18nKey="common:upload" defaults="Upload" />
                </>
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
